import { EmptyState } from "../lib/EmptyState.tsx";
import { TileRow } from "../lib/Tiles.tsx";
import { ATTRIBUTION_FEES_COPY } from "./estate.constants.ts";
import { useAttributionFeesController } from "./useAttributionFeesController.ts";
import "./estate.css";

/**
 * The studio's authentication fees, on the Transaction Centre — OND-70.
 *
 * The fee ledger shipped under OND-60 on the Verifications tab, beside each
 * case, which is where the two acts on a fee belong. What a director opening
 * the Transaction Centre wanted was the MONEY view of the same rows: what has
 * been recorded as owed and as paid, per currency, with the list under it.
 * This draws that and offers no control — the copy says where the controls are.
 *
 * Sits above the resale settlements because both are "what the registry has
 * recorded as owed to this studio", read from two ledgers.
 */
export function AttributionFees() {
  const { view } = useAttributionFeesController();

  return (
    <section className="estate__fees" aria-labelledby="estate-fees-heading">
      <h2 id="estate-fees-heading" className="estate__fees-heading">
        {ATTRIBUTION_FEES_COPY.heading}
      </h2>
      <p className="estate__fees-lead">{ATTRIBUTION_FEES_COPY.lead}</p>

      {view.kind === "loading" && (
        <p className="estate__note" role="status">
          {view.message}
        </p>
      )}

      {view.kind === "error" && (
        <div className="estate__note estate__note--error" role="alert">
          <h3 className="estate__note-title">{view.title}</h3>
          <p>{view.message}</p>
        </div>
      )}

      {view.kind === "empty" && (
        <EmptyState className="estate__empty" lead={view.message} detail={view.detail} />
      )}

      {view.kind === "ready" && (
        <>
          <div className="estate__fees-totals" aria-label={ATTRIBUTION_FEES_COPY.totalsLabel}>
            <TileRow tiles={view.totals} />
          </div>
          <p className="estate__summary">{view.summary}</p>
          <ul className="estate__fees-list">
            {view.rows.map((row) => (
              <li key={row.requestId} className="estate__fee">
                <div>
                  <p className="estate__fee-title">
                    {row.title}
                    {row.claimedArtistName ? ` · ${row.claimedArtistName}` : ""}
                  </p>
                  <p className="estate__fee-meta">
                    {row.askedBy} · {row.answered}
                  </p>
                  {row.fee.paid && (
                    <p className="estate__fee-meta">
                      {row.fee.paid}
                      {row.fee.paidByName ? ` · ${row.fee.paidByName}` : ""}
                      {row.fee.paidNote ? ` · ${row.fee.paidNote}` : ""}
                    </p>
                  )}
                  {row.fee.latestCorrectionReason && (
                    <p className="estate__fee-meta">{row.fee.latestCorrectionReason}</p>
                  )}
                </div>
                <div className="estate__fee-amount">
                  <strong>{row.fee.amount}</strong>
                  <span className="estate__fee-standing">{row.fee.standingLabel}</span>
                </div>
              </li>
            ))}
          </ul>
          <p className="estate__fees-where">{ATTRIBUTION_FEES_COPY.where}</p>
        </>
      )}
    </section>
  );
}
